import { useMemo, useState } from "react";
import { Box, Grid, TextField, Typography, FormControl, FormControlLabel, Switch } from "@mui/material";
import intl from "components/i18n/ReactIntlWrapper";
import StatusSelect from "./Selects/StatusSelect";
import ScopeMultiSelect from "./Selects/ScopeMultiSelect";
import AssignedUsers from "./Users/AssignedUsers";
import TransferList from "components/transferList/TransferList";
import { RoleDetailInput } from "./RoleDetail";
import { ScopeDTO, UserEntityDetails } from "axiosApi/models";

interface RoleFormProps {
  errors: Record<string, string> | Record<string, any>;
  input: RoleDetailInput | null;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleStatusChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: boolean | null) => void;
  disabled?: boolean;
  scopes?: ScopeDTO[] | null;
  idNumber?: number | null;
  users?: UserEntityDetails[] | null;
  handleScopeChange: (scopes: ScopeDTO[] | null) => void;
  selectedScopes?: ScopeDTO[] | null;
}

const RoleForm: React.FC<RoleFormProps> = ({
  errors,
  input,
  handleInputChange,
  handleStatusChange,
  disabled,
  scopes,
  idNumber,
  users,
  handleScopeChange,
  selectedScopes,
}) => {
  const [searchedUser, setSearchedUser] = useState<string>("");
  const scopesIntersection = useMemo(() => {
    if (!scopes || !selectedScopes) return [];
    const selectedScopeIds = new Set(selectedScopes.map((s) => s.id));
    return scopes.filter((scope) => !selectedScopeIds.has(scope.id));
  }, [scopes, selectedScopes]);

  return (
    <>
      {idNumber ? (
        <Box component="form" >
          <Grid container spacing={2} sx={{ width: '100%', margin: '0 auto', marginBottom: 3 }}>
            <Grid size={{xs: 12, md: 5 }}>
              <TextField
                fullWidth
                id="roleName"
                name="name"
                label={intl.get("roleForm.name")}
                placeholder={intl.get("roleForm.placeholder.name")}
                value={input?.name}
                onChange={handleInputChange}
                disabled={disabled}
                error={Boolean(errors.name)}
                helperText={errors.name || ""}
              />
            </Grid>

            <Grid size={{xs: 12, md: 5 }}>
              <TextField
                fullWidth
                id="roleDescription"
                name="description"
                label={intl.get("roleForm.description")}
                placeholder={intl.get("roleForm.placeholder.description")}
                value={input?.description}
                onChange={handleInputChange}
                disabled={disabled}
              />
            </Grid>

            <Grid size={{xs: 12, md: 2 }}>
              <FormControlLabel
                control={ <Switch checked={Boolean(input.active)} onChange={(e, v) => handleStatusChange(e, v)} /> }
                label={input.active ? intl.get('statusSelect.active') : intl.get('statusSelect.inactive')}
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={4}
            display={'flex'}
            direction={{ xs: 'column', md: 'row'}}
            paddingBottom={12}
            height={{ xs: '100%', sm: '400px', md: '450px', lg: '500px' }}
          >
            <Grid
              size={{ xs: 12, md: 7 }}
              height={{ xs: '600px', md:'100%' }}
              marginBottom={{ xs: 15, md: 0 }}
            >
              <Typography variant="subtitle1" mb={1}>
                {intl.get("roleForm.assignment.title")}
              </Typography>
              <Typography variant="body2" mb={2}>
                {intl.get("roleForm.assignment.message")}
              </Typography>
              {(scopes && selectedScopes) ? (
                <Box height={'100%'}>
                  <TransferList
                    transferAll={false}
                    leftItems={scopesIntersection}
                    rightItems={selectedScopes}
                    getLabel={(option) => option.scope}
                    onRightChange={handleScopeChange}
                    readOnly={disabled}
                  />
                </Box>
              ) : null}
            </Grid>

            <Grid size={{xs: 12, md: 5 }} height={{ xs: '500px', md:'100%' }}>
              <Typography variant="subtitle1" mb={1}>
                {intl.get("roleForm.userAssignment.title")}
              </Typography>
              <Typography variant="body2" mb={2}>
                {intl.get("roleForm.userAssignment.message")}
              </Typography>
              <AssignedUsers
                users={users}
                idNumber={idNumber}
                searchedUser={searchedUser}
                setSearchedUser={setSearchedUser}
              />
            </Grid>
          </Grid>
        </Box>

      ) : (
        <Box component="form">
          <Grid container spacing={2} sx={{ width: '100%', margin: '0 auto', marginBottom: 3 }}>
            <Grid size={{xs: 12, md: 5 }}>
              <TextField
                fullWidth
                id="roleName"
                name="name"
                label={intl.get("roleForm.name")}
                placeholder={intl.get("roleForm.placeholder.name")}
                value={input?.name}
                onChange={handleInputChange}
                disabled={disabled}
                error={Boolean(errors.name)}
                helperText={errors.name || ""}
              />
            </Grid>

            <Grid size={{xs: 12, md: 5 }}>
              <TextField
                fullWidth
                id="roleDescription"
                name="description"
                label={intl.get("roleForm.description")}
                placeholder={intl.get("roleForm.placeholder.description")}
                value={input?.description}
                onChange={handleInputChange}
                disabled={disabled}
              />
            </Grid>

            <Grid size={{xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={ <Switch onChange={(e, v) => handleStatusChange(e, v)} /> }
                  label={input.active ? intl.get('statusSelect.active') : intl.get('statusSelect.inactive')}
                />
              </FormControl>
            </Grid>

            <Grid size={12} paddingBottom={8} spacing={2} height={{ xs: '600px', sm: '400px', md: '450px', lg: '500px' }}>
              <Typography variant="subtitle1">
                {intl.get("roleForm.assignment.title")}
              </Typography>
              <Typography variant="body2" mb={2}>
                {intl.get("roleForm.assignment.message")}
              </Typography>
              {scopes ? (
                <Box height={'100%'}>
                  <TransferList
                    transferAll={false}
                    leftItems={scopes}
                    rightItems={selectedScopes || []}
                    getLabel={(option) => option.scope}
                    onRightChange={handleScopeChange}
                  />
                </Box>
              ) : null}
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default RoleForm;
